// Re-export enhanced email validation from security utils
export { validateEmail } from './security';

/**
 * File type validation with magic number (file signature) checking
 * Only allows: PDF, DOCX, DOC, RTF, TXT, JPEG, PNG, TIFF
 */
export const validateFileType = async (file: File): Promise<boolean> => {
    // First check MIME type and extension
    const allowedTypes = [
        'application/pdf',
        'application/msword', // .doc
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
        'application/rtf', // .rtf
        'text/plain', // .txt
        'image/jpeg',
        'image/png',
        'image/tiff',
        'image/tif',
    ];

    const allowedExtensions = /\.(pdf|doc|docx|rtf|txt|jpeg|jpg|png|tiff|tif)$/i;

    // Basic validation
    const hasMimeType = allowedTypes.includes(file.type);
    const hasExtension = allowedExtensions.test(file.name);

    if (!hasMimeType && !hasExtension) {
        return false;
    }

    // Magic number validation for enhanced security
    return await validateFileSignature(file);
};

/**
 * Validate file using magic numbers (file signatures)
 * @param file - File to validate
 * @returns Promise<boolean> - true if file signature matches allowed types
 */
export const validateFileSignature = async (file: File): Promise<boolean> => {
    try {
        // Read first 512 bytes to check file signature
        const buffer = await file.slice(0, 512).arrayBuffer();
        const bytes = new Uint8Array(buffer);

        // Define file signatures (magic numbers)
        const signatures = {
            // PDF files
            pdf: [0x25, 0x50, 0x44, 0x46], // %PDF

            // Microsoft Office files (.doc)
            doc: [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1], // OLE2 signature

            // ZIP-based files (.docx, .xlsx, .pptx) - all use ZIP format
            zip: [0x50, 0x4b, 0x03, 0x04], // ZIP signature (for .docx)
            zipAlt: [0x50, 0x4b, 0x05, 0x06], // Alternative ZIP signature
            zipAlt2: [0x50, 0x4b, 0x07, 0x08], // Another ZIP signature

            // RTF files
            rtf: [0x7b, 0x5c, 0x72, 0x74, 0x66], // {\rtf

            // Text files - more flexible validation since they can start with various characters
            // We'll handle TXT files separately as they don't have a reliable magic number

            // Image files
            jpeg: [0xff, 0xd8, 0xff], // JPEG
            png: [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a], // PNG
            tiff1: [0x49, 0x49, 0x2a, 0x00], // TIFF (little endian)
            tiff2: [0x4d, 0x4d, 0x00, 0x2a], // TIFF (big endian)
        };

        // Special handling for TXT files - validate as text content
        if (file.name.toLowerCase().endsWith('.txt')) {
            return await validateTextFile(file);
        }

        // Check if file matches any allowed signature
        const matchesSignature = Object.values(signatures).some((signature) =>
            signature.every((byte, index) => bytes[index] === byte),
        );

        // Special handling for DOCX files (need to check ZIP + internal structure)
        if (bytes[0] === 0x50 && bytes[1] === 0x4b && file.name.toLowerCase().endsWith('.docx')) {
            return await validateDocxStructure(file);
        }

        return matchesSignature;
    } catch (error) {
        console.error('Error validating file signature:', error);
        return false;
    }
};

/**
 * Validate TXT files by checking if content is primarily text
 * @param file - File to validate as text
 * @returns Promise<boolean> - true if valid text file
 */
async function validateTextFile(file: File): Promise<boolean> {
    try {
        // Read first 1024 bytes to check if content is text
        const buffer = await file.slice(0, 1024).arrayBuffer();
        const bytes = new Uint8Array(buffer);

        // Check for binary content - if too many non-printable characters, reject
        let nonPrintableCount = 0;
        for (let i = 0; i < bytes.length; i++) {
            const byte = bytes[i];
            // Allow common printable ASCII (32-126), newlines (10, 13), tabs (9)
            if (!(byte >= 32 && byte <= 126) && byte !== 10 && byte !== 13 && byte !== 9) {
                nonPrintableCount++;
            }
        }

        // If more than 20% non-printable characters, likely not a text file
        const threshold = bytes.length * 0.2;
        return nonPrintableCount <= threshold;
    } catch (error) {
        console.error('Error validating text file:', error);
        return false;
    }
}

/**
 * Additional validation for DOCX files to ensure they're not malicious ZIP files
 * @param file - File to validate as DOCX
 * @returns Promise<boolean> - true if valid DOCX structure
 */
async function validateDocxStructure(file: File): Promise<boolean> {
    try {
        // For a proper DOCX file, we would need to:
        // 1. Extract and validate the ZIP structure
        // 2. Check for required DOCX files like [Content_Types].xml, word/document.xml
        //
        // For now, we'll do basic ZIP validation and assume if it's a ZIP
        // with .docx extension from a trusted source, it's valid

        // This is a simplified check - in production, you might want to use
        // a library like JSZip to properly validate the DOCX structure
        return file.name.toLowerCase().endsWith('.docx');
    } catch (error) {
        console.error('Error validating DOCX structure:', error);
        return false;
    }
}

export const validateFileSize = (file: File, maxSizeMB?: number): boolean => {
    // REMOVED: Default size limits - no client-side file size restrictions
    // The backend will handle size validation based on business rules
    // This allows enterprise users to upload large files without arbitrary limits

    // Only validate if maxSizeMB is explicitly provided
    if (!maxSizeMB) {
        return true; // No size limit by default
    }

    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    return file.size <= maxSizeBytes;
};

export const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const generateFileId = (): string => {
    return `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};
